// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { styledScrollbar } from "@bg/XDL/Utilities/sharedStyles";
import Notify from "@bgs/App/Components/Notify/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface AppointmentItemStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    address?: TypographyPresentationProps;
    bookAppointmentLink?: LinkPresentationProps;
}

interface XngBookingAppointmentsStyles {
    wrapper?: InjectableCss;
    header?: TypographyPresentationProps;
    content?: InjectableCss;
    item?: AppointmentItemStyles;
    spinnerWrapper?: InjectableCss;
}

const styles: XngBookingAppointmentsStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            background: ${getColor("common.ui4")};
        `,
    },
    header: {
        variant: "h4",
        css: css`
            width: 100%;
            margin-bottom: 0;
            padding: 0 40px;
        `,
    },
    content: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            overflow-y: auto;
            overflow-x: hidden;
            width: 100%;
            padding: 40px;
            ${styledScrollbar(6)};
        `,
    },
    item: {
        wrapper: {
            css: css`
                display: flex;
                align-items: flex-start;
                flex-direction: column;
                margin-bottom: 37px;
            `,
        },
        title: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.semiBold,
            lineHeight: fontConfig.lineHeight.singleLine,
        },
        address: {
            size: fontConfig.fontSize.small,
            lineHeight: fontConfig.lineHeight.singleLine,
        },
        bookAppointmentLink: {
            css: css`
                margin-top: 5px;
            `,
            typographyProps: {
                weight: fontConfig.weight.semiBold,
                css: css`
                    font-size: ${fontConfig.fontSize.xxSmall}px;
                    letter-spacing: 0.86px;
                    line-height: ${fontConfig.lineHeight.xSmall};
                    &&&& {
                        color: ${getColor("text.link")};
                    }
                `,
            },
            icon: {
                iconProps: {
                    src: "Calendar",
                    size: 14,
                    css: css`
                        svg {
                            fill: ${getColor("tertiary")};
                        }
                    `,
                },
            },
        },
    },
    spinnerWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: ${getColor("primary.contrast")};
                opacity: 0.75;
                z-index: 1;
            }

            & ${/* sc-selector */ LoadingSpinnerStyle} {
                z-index: 1300;
            }
        `,
    },
};

interface AppointmentItemProps {
    title: string;
    address: string;
    bookingPageUrl: string;
}

const AppointmentItem: React.FC<AppointmentItemProps> = ({ title, address, bookingPageUrl }) => {
    const [showModal, setShowModal] = React.useState(false);

    const openModal = () => setShowModal(true);
    const closeModal = () => setShowModal(false);

    return (
        <StyledWrapper {...styles.item?.wrapper}>
            <Typography {...styles.item?.title}>{title}</Typography>
            <Typography {...styles.item?.address}>{address}</Typography>
            <Link
                id="Setmore_button_iframe"
                style={{
                    float: "none",
                }}
                onClick={openModal}
                {...styles.item?.bookAppointmentLink}
            >
                {translate("Book Appointment")}
            </Link>
            <Modal sizeVariant="medium" headline={title} isOpen={showModal} handleClose={closeModal}>
                {(bookingPageUrl && bookingPageUrl !== "" && (
                    <iframe src={bookingPageUrl} frameBorder="0" style={{ width: "100%", height: "600px" }}></iframe>
                )) || <Typography {...styles.item?.address}>{translate("Booking not available right now")}</Typography>}
            </Modal>
        </StyledWrapper>
    );
};

interface OwnProps {
    dealers: DealerModel[];
    isLoading: boolean;
}

type Props = OwnProps;

const XngBookingAppointments = ({ dealers, isLoading }: Props) => {
    if (isLoading) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if ((!dealers || !dealers.length) && !isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.content}>
                    <Notify messageType="info" body={translate("No Locations Found")} />
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.header}>{translate("Book Appointment")}</Typography>
            <StyledWrapper {...styles.content}>
                {dealers.map(dealer => {
                    const {
                        id,
                        city,
                        state,
                        address1,
                        properties: { bookingPageUrl },
                    } = dealer;
                    const title = `${city}, ${state}`;

                    return (
                        <AppointmentItem key={id} title={title} address={address1} bookingPageUrl={bookingPageUrl} />
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default XngBookingAppointments;
